import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { useHttp } from '@/hooks/http';

import type { IzziSaleOrder, PaginatedIzziSaleOrders } from '@/domain/izzi';

const DEFAULT_PAGE_SIZE = 20;

const EMPTY_PAGE: PaginatedIzziSaleOrders = {
  data: [],
  total: 0,
  page: 0,
  pageSize: DEFAULT_PAGE_SIZE,
};

export const useOrdersSection = () => {
  const http = useHttp();
  const [isLoading, setIsLoading] = useState(false);
  const [downloadingOrderId, setDownloadingOrderId] = useState<string | null>(null);
  const [data, setData] = useState<PaginatedIzziSaleOrders>(EMPTY_PAGE);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [searchCustomerSo, setSearchCustomerSo] = useState('');
  const [searchOrderNumber, setSearchOrderNumber] = useState('');
  const [appliedCustomerSo, setAppliedCustomerSo] = useState('');
  const [appliedOrderNumber, setAppliedOrderNumber] = useState('');

  const fetchOrders = useCallback(async () => {
    setIsLoading(true);

    try {
      const result = await http.izzi.getOrders({
        page: paginationModel.page,
        pageSize: paginationModel.pageSize,
        ...(appliedCustomerSo ? { customerSo: appliedCustomerSo } : {}),
        ...(appliedOrderNumber ? { orderNumber: appliedOrderNumber } : {}),
      });
      setData(result);
    } catch {
      setData(EMPTY_PAGE);
      toast.error('Error al cargar las órdenes de Izzi.');
    } finally {
      setIsLoading(false);
    }
  }, [http.izzi, paginationModel, appliedCustomerSo, appliedOrderNumber]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const onSearchCustomerSoChange = (value: string) => {
    setSearchCustomerSo(value);
  };

  const onSearchOrderNumberChange = (value: string) => {
    setSearchOrderNumber(value);
  };

  const onSearch = () => {
    setAppliedCustomerSo(searchCustomerSo.trim());
    setAppliedOrderNumber(searchOrderNumber.trim());
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const onClearSearch = () => {
    setSearchCustomerSo('');
    setSearchOrderNumber('');
    setAppliedCustomerSo('');
    setAppliedOrderNumber('');
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const onPaginationModelChange = (model: { page: number; pageSize: number }) => {
    setPaginationModel(model);
  };

  const onDownloadShippingReceipt = async (order: IzziSaleOrder) => {
    setDownloadingOrderId(order.id);

    try {
      await http.izzi.downloadShippingReceipt(order);
      toast.success('Acuse de envío descargado correctamente.');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Error al generar el acuse de envío';
      toast.error(message);
    } finally {
      setDownloadingOrderId(null);
    }
  };

  const hasAppliedFilters = !!(appliedCustomerSo || appliedOrderNumber);

  return {
    data,
    isLoading,
    downloadingOrderId,
    paginationModel,
    searchCustomerSo,
    searchOrderNumber,
    appliedCustomerSo,
    appliedOrderNumber,
    hasAppliedFilters,
    onSearchCustomerSoChange,
    onSearchOrderNumberChange,
    onSearch,
    onClearSearch,
    onPaginationModelChange,
    onDownloadShippingReceipt,
    fetchOrders,
  };
};
